import { createContext, useCallback, useContext, useState } from 'react'
import ReactConfetti, { IConfettiOptions } from 'react-confetti'
import { useWindowSize } from 'usehooks-ts'

type TConfittiContext = {
  show: boolean
  showConfetti: (opts?: Partial<IConfettiOptions>) => void
}

export const ConfettiContext = createContext<TConfittiContext>({ show: false, showConfetti: () => {} })

/**
 * Confetti function to draw money bills
 */
export function drawMoney(this: any, ctx: CanvasRenderingContext2D) {
  const w = 40
  const h = w * 0.42508143
  const curveHeight = h / 3
  // draw a rectangle curved in the middle
  ctx.beginPath()
  ctx.moveTo(0, 0)
  ctx.quadraticCurveTo(w / 2, curveHeight, w, 0)
  ctx.lineTo(w, h)
  ctx.quadraticCurveTo(w / 2, h + curveHeight, 0, h)
  ctx.lineTo(0, 0)
  ctx.fillStyle = 'green'
  ctx.fill()
  ctx.closePath()

  ctx.beginPath()
  ctx.ellipse(w / 2, h / 2 + curveHeight / 2, w / 6, h / 3, 0, 0, Math.PI * 2)
  ctx.fillStyle = 'lightgreen'
  ctx.fill()
  ctx.closePath()
}

export function ConfettiContextProvider({ children }: { children: React.ReactNode }) {
  const { height, width } = useWindowSize()
  const [show, setShow] = useState<boolean>(false)
  const [opts, setOpts] = useState<Partial<IConfettiOptions>>({})

  const showConfetti = useCallback(
    (opts: Partial<IConfettiOptions> = {}) => {
      setOpts({
        run: true,
        recycle: false,
        height,
        width,
        ...opts,
        tweenDuration: 1000,
        onConfettiComplete: () => {
          setOpts({})
          setShow(false)
        },
      })
      setShow(true)
    },
    [height, width],
  )

  return (
    <ConfettiContext.Provider value={{ show, showConfetti }}>
      {show && (
        <ReactConfetti style={{ zIndex: 1401, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }} {...opts} />
      )}
      {children}
    </ConfettiContext.Provider>
  )
}

export function useConfetti() {
  const ctx = useContext(ConfettiContext)
  if (!ctx) {
    throw new Error('useConfetti must be used within a ConfettiContextProvider')
  }
  return ctx
}
